/* Tổng đài Sales — hàng đợi gọi, cuộc nhỡ, nhật ký cuộc gọi, số liệu trong ngày (logic thuần, dùng chung dữ liệu với Khách tiềm năng) */
import dayjs from 'dayjs';
import type { ChannelCustomerItem } from '../../../api/ChannelCustomer';
import type { CustomerTagItem } from '../../../api/CustomerTag';
import {
  MISSED, isConnected, isLeadTag, nextStep, tagOf, type LeadLocal, type NextStep, type Touch, missesSinceConnected } from './logic';

export type QueueKind = 'missed' | 'callback' | 'new' | 'retry' | 'confirm';
/** Thứ tự loại việc khi cùng quá hạn / cùng chưa tới hạn: khách tự gọi đến → hẹn gọi lại → khách mới → gọi lại → xác nhận lịch */
export const QUEUE_KINDS: QueueKind[] = ['missed', 'callback', 'new', 'retry', 'confirm'];
export const KIND_META: Record<QueueKind, { label: string; color: string }> = {
  missed: { label: 'Cuộc nhỡ', color: 'red' },
  callback: { label: 'Hẹn gọi lại', color: 'gold' },
  new: { label: 'Khách mới', color: 'cyan' },
  retry: { label: 'Gọi lại', color: 'orange' },
  confirm: { label: 'Xác nhận lịch', color: 'blue' },
};

export interface QItem {
  r: ChannelCustomerItem;
  step: NextStep & { kind: QueueKind };
  late: boolean;
  /** Số cuộc gọi đi liên tiếp chưa liên lạc được */
  tries: number;
  last?: Touch;
}

const isQueueKind = (k: NextStep['kind']): k is QueueKind => (QUEUE_KINDS as string[]).includes(k);

/** Số cuộc gọi đi liên tiếp (mới nhất trước) chưa kết nối */
export const triesOf = (l?: LeadLocal) => {
  const out = (l?.touches ?? []).filter((t) => t.kind === 'call' && t.dir === 'out');
  const i = out.findIndex((t) => isConnected(t.code));
  return i === -1 ? out.length : i;
};

/** Hàng đợi: quá hạn lên đầu → loại việc → hạn gần nhất */
export function buildQueue(rows: ChannelCustomerItem[], byId: Record<number, LeadLocal>, tags: CustomerTagItem[], now: number): QItem[] {
  const out: QItem[] = [];
  rows.forEach((r) => {
    const l = byId[r.id];
    const tag = tagOf(r, l, tags)?.name;
    // Khách đã làm dịch vụ (S7, S8) / chuyển CSKH không còn trong hàng đợi Telesales
    if (!isLeadTag(tag)) return;
    const step = nextStep(r, l, now, tag);
    if (!isQueueKind(step.kind)) return;
    out.push({ r, step: step as QItem['step'], late: !!step.due && step.due < now, tries: Math.max(triesOf(l), missesSinceConnected(r, l)), last: l?.touches[0] });
  });
  return out.sort((a, b) => Number(b.late) - Number(a.late)
    || QUEUE_KINDS.indexOf(a.step.kind) - QUEUE_KINDS.indexOf(b.step.kind)
    || (a.step.due ?? Infinity) - (b.step.due ?? Infinity));
}

export interface CallRow { r: ChannelCustomerItem; t: Touch }

/** Mọi cuộc gọi (đi / đến / nhỡ) từ mốc `from`, mới nhất trước */
export function callLog(rows: ChannelCustomerItem[], byId: Record<number, LeadLocal>, from: number): CallRow[] {
  return rows
    .flatMap((r) => (byId[r.id]?.touches ?? []).filter((t) => t.kind === 'call' && t.at >= from).map((t) => ({ r, t })))
    .sort((a, b) => b.t.at - a.t.at);
}

export interface MissedRow extends CallRow {
  /** Lần gọi lại / nghe máy sau cuộc nhỡ */
  back?: Touch;
}

/** Cuộc gọi nhỡ của khách đã có hồ sơ, kèm lần gọi lại sau đó (nếu có) */
export function missedCalls(rows: ChannelCustomerItem[], byId: Record<number, LeadLocal>, from: number): MissedRow[] {
  return callLog(rows, byId, from)
    .filter(({ t }) => t.code === MISSED)
    .map(({ r, t }) => {
      // touches mới nhất trước → lần gọi gần cuộc nhỡ nhất là phần tử cuối trong các cuộc sau nó
      const after = (byId[r.id]?.touches ?? []).filter((x) => x.kind === 'call' && x.code !== MISSED && x.at > t.at);
      return { r, t, back: after[after.length - 1] };
    });
}

export interface CallKpi {
  total: number;
  out: number;
  inbound: number;
  missed: number;
  /** Cuộc gọi đi đã kết nối */
  connected: number;
  rate: number | null;
  /** Lịch hẹn mới tạo trong ngày (không tính lịch dời) */
  booked: number;
  talkSec: number;
}

export function callKpi(rows: ChannelCustomerItem[], byId: Record<number, LeadLocal>, now: number): CallKpi {
  const from = dayjs(now).startOf('day').valueOf();
  const calls = callLog(rows, byId, from).map((x) => x.t);
  const out = calls.filter((t) => t.dir === 'out');
  const connected = out.filter((t) => isConnected(t.code)).length;
  const booked = rows.reduce((n, r) => n + (byId[r.id]?.appts ?? []).filter((a) => a.createdAt >= from && !a.movedFrom).length, 0);
  return {
    total: calls.length,
    out: out.length,
    inbound: calls.filter((t) => t.dir === 'in' && t.code !== MISSED).length,
    missed: calls.filter((t) => t.code === MISSED).length,
    connected,
    rate: out.length ? Math.round((connected / out.length) * 100) : null,
    booked,
    talkSec: calls.reduce((s, t) => s + (t.dur ?? 0), 0),
  };
}

/** "1 giờ 05 phút" / "12 phút" cho tổng thời gian đàm thoại */
export const talkText = (sec: number) => {
  const m = Math.round(sec / 60);
  return m < 60 ? `${m} phút` : `${Math.floor(m / 60)} giờ ${String(m % 60).padStart(2, '0')} phút`;
};
